import { useEffect, useState } from "react";
import { appCRUD } from "../../../actions/app";
import { char } from "../../../utils";
import {
  Button, Table, Tag, Card, Row, Col, Modal, Form, Input,
  Select, Switch, InputNumber, Tooltip, Space, Popconfirm
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined,
  InfoCircleOutlined, SaveOutlined
} from "@ant-design/icons";
import { useNotification } from "../../../utils/notificationProvider";

const { Option } = Select;
const { TextArea } = Input;

const CONSTRAINT_TYPES = [
  { value: 'EXCLUSIVE', label: 'Exclusive', color: 'red', description: 'Room is ONLY for specified programmes/courses' },
  { value: 'PREFERRED', label: 'Preferred', color: 'blue', description: 'Try this room first, but allow overflow to others' },
  { value: 'BLOCKED', label: 'Blocked', color: 'orange', description: 'Room is NOT allowed for specified programmes/courses' },
];

const APPLY_TO_OPTIONS = [
  { value: 'PROGRAMME', label: 'Programme' },
  { value: 'COURSE', label: 'Course Code' },
  { value: 'BUILDING', label: 'Building Block' },
  { value: 'STUDY_LEVEL', label: 'Study Level' },
];

const STUDY_LEVELS = [
  { value: 'CERTIFICATE', label: 'Certificate' },
  { value: 'DIPLOMA', label: 'Diploma' },
  { value: 'BACHELOR', label: 'Bachelor' },
  { value: 'MASTER', label: 'Master' },
  { value: 'PHD', label: 'PhD' },
];

export const RoomConstraints = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();
  const [applyTo, setApplyTo] = useState('PROGRAMME');

  const openNotification = useNotification();

  // Load data
  useEffect(() => {
    loadConstraints();
    loadRooms();
    loadProgrammes();
    loadBuildings();
  }, []);

  const loadConstraints = () => {
    setLoading(true);
    appCRUD.loadRoomConstraints(100, 0)
      .then((resp) => {
        setData(resp.data.results || resp.data || []);
      })
      .catch((error) => {
        openNotification("error", "Error", error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const loadRooms = () => {
    appCRUD.loadRoom(200, 0, "", "code")
      .then((resp) => {
        setRooms(resp.data.results || resp.data || []);
      })
      .catch(console.error);
  };

  const loadProgrammes = () => {
    appCRUD.loadProgramme(200, 0, "", "code")
      .then((resp) => {
        setProgrammes(resp.data.results || resp.data || []);
      })
      .catch(console.error);
  };

  const loadBuildings = () => {
    appCRUD.loadBuilding(200, 0, "", "code")
      .then((resp) => {
        setBuildings(resp.data.results || resp.data || []);
      })
      .catch(console.error);
  };

  const handleAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ priority: 10, is_active: true, apply_to: 'PROGRAMME' });
    setApplyTo('PROGRAMME');
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    setApplyTo(record.apply_to);
    form.setFieldsValue({
      room: record.room?.id,
      constraint_type: record.constraint_type,
      apply_to: record.apply_to,
      programme: record.programme?.id,
      course_code: record.course_code,
      building: record.building?.id,
      study_level: record.study_level,
      priority: record.priority,
      description: record.description,
      is_active: record.is_active,
    });
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    appCRUD.deleteRoomConstraint(id)
      .then(() => {
        openNotification("success", "Success", "Constraint deleted");
        loadConstraints();
      })
      .catch((error) => {
        openNotification("error", "Error", error.message);
      });
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      const saveData = {
        room: values.room,
        constraint_type: values.constraint_type,
        apply_to: values.apply_to,
        priority: values.priority || 10,
        description: values.description,
        is_active: values.is_active !== false,
      };

      // Set the appropriate field based on apply_to
      if (values.apply_to === 'PROGRAMME') {
        saveData.programme = values.programme;
      } else if (values.apply_to === 'COURSE') {
        saveData.course_code = values.course_code;
      } else if (values.apply_to === 'BUILDING') {
        saveData.building = values.building;
      } else if (values.apply_to === 'STUDY_LEVEL') {
        saveData.study_level = values.study_level;
      }

      const promise = editingId
        ? appCRUD.updateRoomConstraint(editingId, saveData)
        : appCRUD.addRoomConstraint(saveData);

      promise
        .then(() => {
          openNotification("success", "Success", editingId ? "Constraint updated" : "Constraint created");
          setModalOpen(false);
          loadConstraints();
        })
        .catch((error) => {
          openNotification("error", "Error", error.response?.data?.detail || error.message);
        });
    });
  };

  const getConstraintTarget = (record) => {
    if (record.apply_to === 'PROGRAMME' && record.programme) {
      return <Tag color="blue">{record.programme.code}</Tag>;
    }
    if (record.apply_to === 'COURSE' && record.course_code) {
      return <Tag color="green">{record.course_code}</Tag>;
    }
    if (record.apply_to === 'BUILDING' && record.building) {
      return <Tag color="purple">{record.building.code}</Tag>;
    }
    if (record.apply_to === 'STUDY_LEVEL' && record.study_level) {
      return <Tag color="orange">{record.study_level}</Tag>;
    }
    return <Tag>N/A</Tag>;
  };

  const columns = [
    {
      title: "#",
      key: "sn",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Room",
      dataIndex: ["room", "code"],
      key: "room",
      width: 120,
      render: (code, record) => (
        <Tooltip title={record.room?.name}>
          <strong>{code}</strong>
        </Tooltip>
      ),
    },
    {
      title: "Constraint Type",
      dataIndex: "constraint_type",
      key: "constraint_type",
      width: 130,
      render: (type) => {
        const config = CONSTRAINT_TYPES.find(c => c.value === type);
        return (
          <Tooltip title={config?.description}>
            <Tag color={config?.color}>{config?.label || type}</Tag>
          </Tooltip>
        );
      },
      filters: CONSTRAINT_TYPES.map(t => ({ text: t.label, value: t.value })),
      onFilter: (value, record) => record.constraint_type === value,
    },
    {
      title: "Applies To",
      dataIndex: "apply_to",
      key: "apply_to",
      width: 120,
      render: (applyTo) => {
        const config = APPLY_TO_OPTIONS.find(o => o.value === applyTo);
        return config?.label || applyTo;
      },
    },
    {
      title: "Target",
      key: "target",
      width: 150,
      render: (_, record) => getConstraintTarget(record),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 80,
      align: "center",
      sorter: (a, b) => a.priority - b.priority,
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      align: "center",
      render: (active) => (
        <Tag color={active ? "success" : "default"}>{active ? "Yes" : "No"}</Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this constraint?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Group constraints by room for summary
  const roomSummary = {};
  data.forEach(constraint => {
    const roomCode = constraint.room?.code;
    if (roomCode) {
      if (!roomSummary[roomCode]) {
        roomSummary[roomCode] = { exclusive: 0, preferred: 0, blocked: 0 };
      }
      if (constraint.constraint_type === 'EXCLUSIVE') roomSummary[roomCode].exclusive++;
      if (constraint.constraint_type === 'PREFERRED') roomSummary[roomCode].preferred++;
      if (constraint.constraint_type === 'BLOCKED') roomSummary[roomCode].blocked++;
    }
  });

  return (
    <div className="container-fluid">
      <div className="row mb-3">
        <div className="col-12">
          <h5 className="fw-bold text-dark mb-0">
            <SettingOutlined className="me-2" />
            Room Constraints
          </h5>
          <p className="text-muted small mb-0">
            Configure which rooms are available for specific programmes, courses, or study levels
          </p>
        </div>
        <hr className="mt-3" />
      </div>

      {/* Info Cards */}
      <Row gutter={16} className="mb-4">
        <Col xs={24} sm={8}>
          <Card size="small">
            <div className="d-flex align-items-center">
              <Tag color="red" className="me-2">EXCLUSIVE</Tag>
              <span className="small text-muted">
                Room is ONLY for specified targets. Others cannot use it.
              </span>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div className="d-flex align-items-center">
              <Tag color="blue" className="me-2">PREFERRED</Tag>
              <span className="small text-muted">
                Try this room first, but allow overflow to other rooms.
              </span>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div className="d-flex align-items-center">
              <Tag color="orange" className="me-2">BLOCKED</Tag>
              <span className="small text-muted">
                Room is NOT allowed for specified targets.
              </span>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Card>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span className="text-muted">
            {data.length} constraint{data.length !== 1 ? 's' : ''} configured
          </span>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Add Constraint
          </Button>
        </div>

        <Table
          loading={loading}
          columns={columns}
          dataSource={data}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} constraints`,
          }}
          size="small"
          bordered
        />
      </Card>

      {/* Quick Setup Section */}
      <Card title="Quick Setup Examples" className="mt-4" size="small">
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Card size="small" type="inner" title="Master-Only Rooms">
              <p className="small text-muted mb-2">
                Rooms like TRANS and GIS Lab can be set as EXCLUSIVE for Master programmes.
              </p>
              <Button
                size="small"
                onClick={() => {
                  form.resetFields();
                  form.setFieldsValue({
                    constraint_type: 'EXCLUSIVE',
                    apply_to: 'STUDY_LEVEL',
                    study_level: 'MASTER',
                    priority: 100,
                    is_active: true,
                  });
                  setApplyTo('STUDY_LEVEL');
                  setModalOpen(true);
                }}
              >
                Create Master-Only Constraint
              </Button>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small" type="inner" title="Computing Block Preference">
              <p className="small text-muted mb-2">
                Set computing rooms as PREFERRED for CS courses (try first, overflow allowed).
              </p>
              <Button
                size="small"
                onClick={() => {
                  form.resetFields();
                  form.setFieldsValue({
                    constraint_type: 'PREFERRED',
                    apply_to: 'COURSE',
                    course_code: 'CS%',
                    priority: 50,
                    is_active: true,
                  });
                  setApplyTo('COURSE');
                  setModalOpen(true);
                }}
              >
                Create CS Preference
              </Button>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small" type="inner" title="Block Room for Programme">
              <p className="small text-muted mb-2">
                Prevent specific programmes from being scheduled in certain rooms.
              </p>
              <Button
                size="small"
                onClick={() => {
                  form.resetFields();
                  form.setFieldsValue({
                    constraint_type: 'BLOCKED',
                    apply_to: 'PROGRAMME',
                    priority: 10,
                    is_active: true,
                  });
                  setApplyTo('PROGRAMME');
                  setModalOpen(true);
                }}
              >
                Create Block Constraint
              </Button>
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingId ? "Edit Room Constraint" : "Add Room Constraint"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" icon={<SaveOutlined />} onClick={handleSave}>
            Save
          </Button>,
        ]}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="room"
                label="Room"
                rules={[{ required: true, message: "Please select a room" }]}
              >
                <Select
                  placeholder="Select room"
                  showSearch
                  optionFilterProp="children"
                >
                  {rooms.map((room) => (
                    <Option key={room.id} value={room.id}>
                      {room.code} - {char.toTitleCase(room.name || "")}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="constraint_type"
                label="Constraint Type"
                rules={[{ required: true, message: "Please select type" }]}
              >
                <Select placeholder="Select type">
                  {CONSTRAINT_TYPES.map((type) => (
                    <Option key={type.value} value={type.value}>
                      <Tag color={type.color}>{type.label}</Tag>
                      <span className="small text-muted ms-2">{type.description}</span>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="apply_to"
                label="Applies To"
                rules={[{ required: true, message: "Please select" }]}
              >
                <Select
                  placeholder="Select what this applies to"
                  onChange={(value) => setApplyTo(value)}
                >
                  {APPLY_TO_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              {applyTo === 'PROGRAMME' && (
                <Form.Item
                  name="programme"
                  label="Programme"
                  rules={[{ required: true, message: "Please select programme" }]}
                >
                  <Select placeholder="Select programme" showSearch optionFilterProp="children">
                    {programmes.map((prog) => (
                      <Option key={prog.id} value={prog.id}>
                        {prog.code} - {char.toTitleCase(prog.name || "")}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              {applyTo === 'COURSE' && (
                <Form.Item
                  name="course_code"
                  label="Course Code Pattern"
                  rules={[{ required: true, message: "Please enter course code" }]}
                  extra="Use % as wildcard (e.g., CS% for all CS courses)"
                >
                  <Input placeholder="e.g., CS%, MT 1101" />
                </Form.Item>
              )}
              {applyTo === 'BUILDING' && (
                <Form.Item
                  name="building"
                  label="Building"
                  rules={[{ required: true, message: "Please select building" }]}
                >
                  <Select placeholder="Select building" showSearch optionFilterProp="children">
                    {buildings.map((bldg) => (
                      <Option key={bldg.id} value={bldg.id}>
                        {bldg.code} - {char.toTitleCase(bldg.name || "")}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              {applyTo === 'STUDY_LEVEL' && (
                <Form.Item
                  name="study_level"
                  label="Study Level"
                  rules={[{ required: true, message: "Please select level" }]}
                >
                  <Select placeholder="Select study level">
                    {STUDY_LEVELS.map((level) => (
                      <Option key={level.value} value={level.value}>
                        {level.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="priority"
                label={
                  <span>
                    Priority
                    <Tooltip title="Higher priority constraints are applied first. Use higher values (50-100) for important rules.">
                      <InfoCircleOutlined className="ms-1 text-muted" />
                    </Tooltip>
                  </span>
                }
              >
                <InputNumber min={1} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Optional description for this constraint" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RoomConstraints;
